import { Form, FormikProvider, useFormik } from 'formik';
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import Button from '@mui/material/Button';
import { LoadingButton } from '@mui/lab';
import * as Yup from 'yup';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import { useTranslation } from 'react-i18next';
import { ProductScheduleTemplate } from '../../@types/model/productScheduleTemplate';
import { v4 } from 'uuid';
import { ProductSchedule, ProductScheduleItem } from '../../@types/model/productSchedule';
import { productScheduleTemplateState } from 'recoil/productScheduleTemplateState';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useEffect, useState } from 'react';
import { Device, RefactoredDevice } from '../../@types/model/device';
import { deviceState, getDevices } from 'recoil/deviceState';
import ProductScheduleItemForm from './ProductScheduleItemForm';
import SelectDevicesModal from 'pages/DeviceGroups/partials/SelectDevicesModal';

import '../../assets/css/productScheduleForm.scss';
import SvgIconStyle from 'components/SvgIconStyle';
import refactorDevices from 'components/device/Components/refactorDevices';

export interface ProductScheduleFormProps {
  productSchedule: ProductSchedule;
  edit: boolean;
  submitHandler: (group: ProductSchedule) => void;
  loading: boolean;
}

type createOrUpdateProductScheduleInitialValues = {
  guid: string;
  template: string;
  units: number;
  devices: string[];
  reference: string;
  schedule: ProductScheduleItem[];
};

const ProductScheduleForm = (props: ProductScheduleFormProps) => {
  const isMountedRef = useIsMountedRef();
  const { t } = useTranslation();
  const productScheduleTemplates: ProductScheduleTemplate[] = useRecoilValue(
    productScheduleTemplateState
  );

  const devices: Device[] = useRecoilValue(deviceState);
  const setDevices = useSetRecoilState(deviceState);

  const [refactoredDevices, setRefactoredDevices] = useState<RefactoredDevice[]>([]);

  const [selectDevicesModal, setSelectDevicesModal] = useState<boolean>(false);

  useEffect(() => {
    getDevices(500).then((devices: Device[]) => {
      setDevices(devices);
      setRefactoredDevices(refactorDevices(devices));
    });
  }, []);

  const createOrUpdateProductScheduleSchema = Yup.object().shape({
    template: Yup.string().required(t('template_is_required')),
    units: Yup.number().required(t('units_is_required')),
    devices: Yup.array().required(t('devices_is_required')).min(1, t('devices_at_least_1')),
    reference: Yup.string().required(t('reference_is_required')),
    schedule: Yup.array().of(
      Yup.object().shape({
        ordinal: Yup.number().required(t('required')),
        product: Yup.string().required(t('required')),
        quantity: Yup.number().required(t('required')),
        consumed: Yup.number().required(t('required'))
      })
    )
  });

  const CreateOrUpdateProductSchedule = useFormik<createOrUpdateProductScheduleInitialValues>({
    initialValues: {
      guid: props.productSchedule.guid,
      template: props.productSchedule.template,
      units: props.productSchedule.configuration.units,
      devices: props.productSchedule.devices,
      reference: props.productSchedule.reference,
      schedule: props.productSchedule.schedule
    },
    validationSchema: createOrUpdateProductScheduleSchema,
    onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
      try {
        props.submitHandler({
          guid: props.productSchedule.guid,
          template: values.template,
          configuration: {
            units: values.units
          },
          devices: values.devices,
          reference: values.reference,
          schedule: values.schedule,
          product_schedule_template: null,
          currentItem: 0,
          state: 'active'
        } as ProductSchedule);
        if (isMountedRef.current) {
          setSubmitting(false);
        }
      } catch (error: any) {
        resetForm();
        if (isMountedRef.current) {
          setSubmitting(false);
        }
      }
    }
  });

  const deleteDeviceFromProductSchedule = (guid: string | undefined) => {
    CreateOrUpdateProductSchedule.setFieldValue(
      'devices',
      CreateOrUpdateProductSchedule.values.devices.filter((device_guid) => {
        if (device_guid === guid) return false;
        return true;
      })
    );
  };

  return (
    <FormikProvider value={CreateOrUpdateProductSchedule}>
      <Form autoComplete="off" noValidate onSubmit={CreateOrUpdateProductSchedule.handleSubmit}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              autoComplete={t('name')}
              type="string"
              label={t('name')}
              {...CreateOrUpdateProductSchedule.getFieldProps('name')}
              error={Boolean(
                CreateOrUpdateProductSchedule.touched.name &&
                  CreateOrUpdateProductSchedule.errors.name
              )}
              helperText={
                CreateOrUpdateProductSchedule.touched.name &&
                CreateOrUpdateProductSchedule.errors.name
              }
            />
          </Grid> */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('template')}</InputLabel>
              <Select
                {...CreateOrUpdateProductSchedule.getFieldProps('template')}
                label={t('template')}
                error={Boolean(
                  CreateOrUpdateProductSchedule.touched.template &&
                    CreateOrUpdateProductSchedule.errors.template
                )}
                style={{ width: '100%' }}
                disabled={props.edit}
              >
                {productScheduleTemplates.map((pst) => {
                  return (
                    <MenuItem key={pst.guid} value={pst.guid}>
                      {pst.name}
                    </MenuItem>
                  );
                })}
              </Select>
              <FormHelperText style={{ color: 'red' }}>
                {CreateOrUpdateProductSchedule.touched.template &&
                  CreateOrUpdateProductSchedule.errors.template}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              disabled={props.edit}
              fullWidth
              autoComplete={t('units')}
              type="string"
              label={t('units')}
              {...CreateOrUpdateProductSchedule.getFieldProps('units')}
              error={Boolean(
                CreateOrUpdateProductSchedule.touched.units &&
                  CreateOrUpdateProductSchedule.errors.units
              )}
              helperText={
                CreateOrUpdateProductSchedule.touched.units &&
                CreateOrUpdateProductSchedule.errors.units
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              disabled={props.edit}
              fullWidth
              autoComplete={t('reference')}
              type="string"
              label={t('reference')}
              {...CreateOrUpdateProductSchedule.getFieldProps('reference')}
              error={Boolean(
                CreateOrUpdateProductSchedule.touched.reference &&
                  CreateOrUpdateProductSchedule.errors.reference
              )}
              helperText={
                CreateOrUpdateProductSchedule.touched.reference &&
                CreateOrUpdateProductSchedule.errors.reference
              }
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <div className="devices">
              <label>{t('devices')}</label>
              {/* {CreateOrUpdateProductSchedule.values.devices.map((guid: string) => {
                let device: Device | undefined = devices.find((dev: Device) => {
                  let is_ref_of = dev.attributes.is_ref_of;
                  if (is_ref_of) {
                    let ref = is_ref_of.split(':')[2];
                    if (ref === guid || dev.guid === guid) return true;
                  }
                  if (dev.guid === guid) return true;
                  return false;
                });
                return (
                  <div key={device?.guid} className="device">
                    {device?.attributes.name}
                  </div>
                );
              })} */}

              {CreateOrUpdateProductSchedule.values.devices
                .filter((guid: string) => {
                  let device: Device | undefined = devices.find((dev: Device) => {
                    let is_ref_of = dev.attributes.is_ref_of;
                    if (is_ref_of) {
                      let ref = is_ref_of.split(':')[2];
                      if (ref === guid || dev.guid === guid) return true;
                    }
                    if (dev.guid === guid) return true;
                    return false;
                  });
                  if (device) return true;
                  return false;
                })
                .map((guid: string) => {
                  let device: RefactoredDevice | undefined = refactoredDevices.find(
                    (dev: Device) => {
                      let is_ref_of = dev.attributes.is_ref_of;
                      if (is_ref_of) {
                        let ref = is_ref_of.split(':')[2];
                        if (ref === guid || dev.guid === guid) return true;
                      }
                      if (dev.guid === guid) return true;
                      return false;
                    }
                  );
                  return (
                    <div key={device?.guid} className="device">
                      {device?.attributes.name}
                      <IconButton
                        onClick={() => deleteDeviceFromProductSchedule(device?.guid_original)}
                        size="small"
                        color="error"
                        sx={{
                          marginLeft: '1px',
                          marginTop: '-4px',
                          marginBottom: '-5px',
                          marginRight: '-12px'
                        }}
                      >
                        <SvgIconStyle
                          src="/static/icons/giraf/remove.svg"
                          sx={{
                            width: '24px',
                            height: '24px',
                            display: 'block',
                            backgroundColor: 'red',
                            color: 'white'
                          }}
                        />
                      </IconButton>
                    </div>
                  );
                })}

              <div>
                <Button
                  variant="contained"
                  onClick={() => {
                    setSelectDevicesModal(true);
                  }}
                >
                  {t('add_devices')}
                </Button>
              </div>

              <SelectDevicesModal
                selectDevicesModal={selectDevicesModal}
                selectedDevices={CreateOrUpdateProductSchedule.values.devices}
                setSelectDevicesModal={(selectDevicesModal) => {
                  setSelectDevicesModal(selectDevicesModal);
                }}
                setSelectedDevices={(setSelectedDevices: string[]) => {
                  // setSelectDevicesModal(selectDevicesModal)
                  CreateOrUpdateProductSchedule.setFieldValue('devices', setSelectedDevices);
                }}
              />
            </div>
            {/* <FormControl fullWidth sx={{ display: 'none' }}>
              <InputLabel>{t('devices')}</InputLabel>
              <Select
                disabled={props.edit}
                multiple
                renderValue={(selected) => {
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value: any) => {
                        let name = devices.find((d) => {
                          if (d.guid === value) return true;
                          return false;
                        })?.attributes.name;
                        return <Chip key={value} label={name} />;
                      })}
                    </Box>
                  );
                }}
                {...CreateOrUpdateProductSchedule.getFieldProps('devices')}
                label={t('devices')}
                error={Boolean(
                  CreateOrUpdateProductSchedule.touched.devices &&
                    CreateOrUpdateProductSchedule.errors.devices
                )}
                style={{ width: '100%' }}
              >
                {devices.map((d) => {
                  return (
                    <MenuItem key={d.guid} value={d.guid}>
                      {d.attributes.name}
                    </MenuItem>
                  );
                })}
              </Select>
              <FormHelperText style={{ color: 'red' }}>
                {CreateOrUpdateProductSchedule.touched.devices &&
                  CreateOrUpdateProductSchedule.errors.devices}
              </FormHelperText>
            </FormControl> */}
          </Grid>
        </Grid>
        {props.edit && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} style={{ marginTop: -20, marginBottom: 10 }}>
              <h3>{t('product_types')}</h3>
            </Grid>
            {CreateOrUpdateProductSchedule.values.schedule.map(
              (item: ProductScheduleItem, indx: number) => {
                return (
                  <Grid
                    item
                    xs={12}
                    md={12}
                    key={item.guid}
                    style={{ marginTop: -10, marginBottom: -10 }}
                  >
                    <ProductScheduleItemForm
                      productScheduleItem={item}
                      submitHandler={(newSchedule: ProductScheduleItem) => {
                        let newSchedules = CreateOrUpdateProductSchedule.values.schedule.map(
                          (s) => {
                            if (s.guid === newSchedule.guid) {
                              return newSchedule;
                            }
                            return s;
                          }
                        );
                        CreateOrUpdateProductSchedule.setFieldValue('schedule', newSchedules);
                      }}
                      deleteHandler={() => {
                        let newSchedules = CreateOrUpdateProductSchedule.values.schedule
                          .filter((s) => {
                            if (s.guid === item.guid) {
                              return false;
                            }
                            return true;
                          })
                          .map((s, index) => {
                            return {
                              ...s,
                              ordinal: index + 1
                            };
                          });
                        CreateOrUpdateProductSchedule.setFieldValue('schedule', newSchedules);
                      }}
                      errors={
                        CreateOrUpdateProductSchedule?.errors?.schedule?.length
                          ? CreateOrUpdateProductSchedule?.errors?.schedule[indx]
                          : false
                      }
                      touched={
                        CreateOrUpdateProductSchedule?.touched?.schedule?.length
                          ? CreateOrUpdateProductSchedule?.touched?.schedule[indx]
                          : false
                      }
                    ></ProductScheduleItemForm>
                  </Grid>
                );
              }
            )}
            <Grid item xs={12} md={12}>
              {/* <FormHelperText style={{ color: 'red', marginBottom: 10 }}>
                {CreateOrUpdateProductSchedule.touched.schedule &&
                  CreateOrUpdateProductSchedule.errors.schedule}
              </FormHelperText> */}
              <Button
                variant="contained"
                onClick={() => {
                  CreateOrUpdateProductSchedule.setFieldValue('schedule', [
                    ...CreateOrUpdateProductSchedule.values.schedule,
                    {
                      guid: v4(),
                      ordinal: CreateOrUpdateProductSchedule.values.schedule.length + 1,
                      product: '',
                      quantity: 0,
                      consumed: 0
                    } as ProductScheduleItem
                  ]);
                }}
              >
                {t('add_product_type')}
              </Button>
            </Grid>
          </Grid>
        )}
        <LoadingButton
          disabled={props.loading}
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={CreateOrUpdateProductSchedule.isSubmitting}
          sx={{ my: 2 }}
        >
          {props.loading ? <CircularProgress size={26} /> : props.edit ? t('update') : t('add')}
        </LoadingButton>
      </Form>
    </FormikProvider>
  );
};

export default ProductScheduleForm;
